import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  Sheet,
  Input,
  Select,
  Option,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Checkbox,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Snackbar,
} from '@mui/joy';
import {
  MagnifyingGlass as Search,
  Tag as Category,
  Receipt,
  TrendUp as TrendingUp,
  TrendDown as TrendingDown,
  Calendar as CalendarToday,
  CheckCircle,
  Warning,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../../lib/apiAccounting';
import type {
  BankTransaction,
  BankAccount,
  ChartOfAccount,
  BankTransactionStatus
} from '../../../types/accounting';

export const BankTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [chartAccounts, setChartAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [selectedAccount, setSelectedAccount] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<BankTransactionStatus | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Selection states
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(new Set());
  const [categorizeModalOpen, setCategorizeModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [createJournalEntry, setCreateJournalEntry] = useState(false);
  const [categorizeNotes, setCategorizeNotes] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    color: 'success' | 'danger' | 'warning';
  }>({ open: false, message: '', color: 'success' });

  const showSnackbar = (message: string, color: 'success' | 'danger' | 'warning') => {
    setSnackbar({ open: true, message, color });
  };

  useEffect(() => {
    fetchAccounts();
    fetchChartAccounts();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [selectedAccount, selectedStatus, searchTerm, startDate, endDate, page]);

  const fetchAccounts = async () => {
    try {
      const response = await apiAccountingClient.getBankAccounts({ is_active: true });
      setAccounts(response.results);
    } catch (err) {
      console.error('Failed to fetch bank accounts:', err);
    }
  };

  const fetchChartAccounts = async () => {
    try {
      const response = await apiAccountingClient.getChartOfAccounts({ is_active: true });
      setChartAccounts(response.results);
    } catch (err) {
      console.error('Failed to fetch chart of accounts:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiAccountingClient.getBankTransactions({
        account: selectedAccount || undefined,
        status: selectedStatus || undefined,
        search: searchTerm || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page,
        page_size: 50,
      });

      setTransactions(response.results);
      setTotalPages(Math.ceil(response.count / 50));
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setError('Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTransactions(new Set(transactions.map(t => t.id)));
    } else {
      setSelectedTransactions(new Set());
    }
  };

  const handleSelectTransaction = (id: number, checked: boolean) => {
    const newSelection = new Set(selectedTransactions);
    if (checked) {
      newSelection.add(id);
    } else {
      newSelection.delete(id);
    }
    setSelectedTransactions(newSelection);
  };

  const handleCategorize = async () => {
    if (!selectedCategory || selectedTransactions.size === 0) return;

    try {
      const result = await apiAccountingClient.categorizeBankTransactions({
        transaction_ids: Array.from(selectedTransactions),
        account_category_id: selectedCategory,
        create_journal_entry: createJournalEntry,
        notes: categorizeNotes,
      });

      if (result.success) {
        showSnackbar(`Successfully categorized ${result.transactions_updated} transactions`, 'success');

        // Clear selection and refresh
        setSelectedTransactions(new Set());
        setCategorizeModalOpen(false);
        setSelectedCategory(null);
        setCategorizeNotes('');
        setCreateJournalEntry(false);

        // Refresh transactions
        await fetchTransactions();
      }
    } catch (err) {
      console.error('Failed to categorize transactions:', err);
      showSnackbar('Failed to categorize transactions. Please try again.', 'danger');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Math.abs(amount));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: BankTransactionStatus): 'neutral' | 'primary' | 'success' | 'warning' => {
    switch (status) {
      case 'PENDING':
        return 'neutral';
      case 'CATEGORIZED':
        return 'primary';
      case 'MATCHED':
        return 'success';
      case 'RECONCILED':
        return 'success';
      default:
        return 'warning';
    }
  };

  if (loading && transactions.length === 0) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography level="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', md: undefined } }}>
          Bank Transactions
        </Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          Review and categorize your imported bank transactions
        </Typography>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <CollapsibleFilters><Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Select
          placeholder="All Accounts"
          value={selectedAccount}
          onChange={(e, value) => setSelectedAccount(value)}
          sx={{ minWidth: 200 }}
        >
          <Option value={null}>All Accounts</Option>
          {accounts.map(account => (
            <Option key={account.id} value={account.id}>
              {account.account_name}
            </Option>
          ))}
        </Select>

        <Select
          placeholder="All Statuses"
          value={selectedStatus}
          onChange={(e, value) => setSelectedStatus(value as BankTransactionStatus | '')}
          sx={{ minWidth: 150 }}
        >
          <Option value="">All Statuses</Option>
          <Option value="PENDING">Pending</Option>
          <Option value="CATEGORIZED">Categorized</Option>
          <Option value="MATCHED">Matched</Option>
          <Option value="RECONCILED">Reconciled</Option>
          <Option value="IGNORED">Ignored</Option>
        </Select>

        <Input
          placeholder="Search transactions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startDecorator={<Search />}
          sx={{ minWidth: 250 }}
        />

        <Input
          type="date"
          placeholder="Start Date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          startDecorator={<CalendarToday />}
        />

        <Input
          type="date"
          placeholder="End Date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          startDecorator={<CalendarToday />}
        />
      </Box></CollapsibleFilters>

      {/* Bulk Actions */}
      {selectedTransactions.size > 0 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.level1', borderRadius: 'sm', border: '1px solid', borderColor: 'primary.outlinedBorder' }}>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            {selectedTransactions.size} transaction{selectedTransactions.size > 1 ? 's' : ''} selected
          </Typography>
          <Button
            size="sm"
            startDecorator={<Category />}
            onClick={() => setCategorizeModalOpen(true)}
          >
            Categorize Selected
          </Button>
        </Box>
      )}

      {/* Transactions Table */}
      <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 820 }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <Checkbox
                  checked={selectedTransactions.size === transactions.length && transactions.length > 0}
                  indeterminate={selectedTransactions.size > 0 && selectedTransactions.size < transactions.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th>Date</th>
              <th>Description</th>
              <th>Category</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((transaction) => (
              <tr key={transaction.id}>
                <td>
                  <Checkbox
                    checked={selectedTransactions.has(transaction.id)}
                    onChange={(e) => handleSelectTransaction(transaction.id, e.target.checked)}
                  />
                </td>
                <td>{formatDate(transaction.transaction_date)}</td>
                <td>
                  <Box>
                    <Typography level="body-sm">{transaction.name}</Typography>
                    {transaction.merchant_name && (
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {transaction.merchant_name}
                      </Typography>
                    )}
                  </Box>
                </td>
                <td>
                  {transaction.category_name ? (
                    <Typography level="body-sm">{transaction.category_name}</Typography>
                  ) : (
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Uncategorized
                    </Typography>
                  )}
                </td>
                <td>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {transaction.is_debit ? (
                      <TrendingDown color="var(--joy-palette-danger-500)" size={18} />
                    ) : (
                      <TrendingUp color="var(--joy-palette-success-500)" size={18} />
                    )}
                    <Typography
                      level="body-sm"
                      sx={{ color: transaction.is_debit ? 'danger.main' : 'success.main' }}
                    >
                      {formatCurrency(transaction.amount)}
                    </Typography>
                  </Box>
                </td>
                <td>
                  <Chip size="sm" color={getStatusColor(transaction.status)}>
                    {transaction.status}
                  </Chip>
                </td>
                <td>
                  {transaction.journal_entry && (
                    <IconButton size="sm" variant="plain">
                      <Receipt />
                    </IconButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
        <Button
          size="sm"
          variant="outlined"
          color="primary"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          Previous
        </Button>
        <Typography level="body-sm" sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
          Page {page} of {totalPages}
        </Typography>
        <Button
          size="sm"
          variant="outlined"
          color="primary"
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
        >
          Next
        </Button>
      </Box>

      {/* Categorize Modal */}
      <Modal open={categorizeModalOpen} onClose={() => setCategorizeModalOpen(false)}>
        <ModalDialog>
          <DialogTitle>Categorize Transactions</DialogTitle>
          <DialogContent>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Category Account</FormLabel>
              <Autocomplete
                options={chartAccounts}
                getOptionLabel={(option) => `${option.account_number} - ${option.name}`}
                value={chartAccounts.find(a => a.id === selectedCategory) || null}
                onChange={(e, value) => setSelectedCategory(value?.id || null)}
                placeholder="Select a category..."
              />
            </FormControl>

            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Notes (Optional)</FormLabel>
              <Input
                value={categorizeNotes}
                onChange={(e) => setCategorizeNotes(e.target.value)}
                placeholder="Add notes..."
              />
            </FormControl>

            <Checkbox
              label="Create journal entries"
              checked={createJournalEntry}
              onChange={(e) => setCreateJournalEntry(e.target.checked)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCategorize} disabled={!selectedCategory}>
              Categorize
            </Button>
            <Button variant="plain" onClick={() => setCategorizeModalOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        color={snackbar.color}
        variant="soft"
        startDecorator={snackbar.color === 'success' ? <CheckCircle /> : <Warning />}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
};
